import { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Pill, Loading, Err, tableStyles, EmptyState } from "./_shared";
import { ShieldCheck, Eye, ScrollText, Lock } from "lucide-react";

export default function SupportMode() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/api/admin/support/active").then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  return (
    <div data-testid="admin-support-mode">
      <PageHead title="Support mode" sub="Time-boxed, audited workspace impersonation"
        right={<Pill tone="warn">{data.available ? "available" : "Turn C"}</Pill>} />

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        <Card title={`Active sessions · ${data.active?.length ?? 0}`}>
          {(!data.active || data.active.length === 0) ? (
            <EmptyState
              title="No active support sessions"
              description="When an admin starts Support Mode on a workspace, the session will appear here with the user, scope, and time remaining."
            />
          ) : (
            <table style={tableStyles.table}>
              <thead><tr>
                <th style={tableStyles.th}>Started</th>
                <th style={tableStyles.th}>Admin</th>
                <th style={tableStyles.th}>Workspace</th>
                <th style={tableStyles.th}>Scope</th>
                <th style={tableStyles.th}>Expires</th>
              </tr></thead>
              <tbody>
                {data.active.map((s) => (
                  <tr key={s.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={{ ...tableStyles.td, fontSize: 11 }}>{(s.started_at || "").slice(0, 19)}</td>
                    <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(s.actor_user_id || "").slice(0, 16)}</td>
                    <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(s.workspace_id || "").slice(0, 16)}</td>
                    <td style={tableStyles.td}><Pill>{s.scope || "read-only"}</Pill></td>
                    <td style={{ ...tableStyles.td, fontSize: 11 }}>{(s.expires_at || "").slice(0, 19)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="How Support Mode works">
          <Tile icon={<ShieldCheck size={14} />} title="Explicit consent" body="Workspace owner approval banner required before scope is granted." />
          <Tile icon={<Lock size={14} />} title="Scoped session token" body="Issues a short-lived JWT with narrow workspace scope — never a long-lived secret." />
          <Tile icon={<Eye size={14} />} title="Read-only by default" body="Admins start in observe mode. Write actions require a step-up confirmation." />
          <Tile icon={<ScrollText size={14} />} title="Full audit trail" body="Every action (and read) is appended to the workspace audit log automatically." />
          <div style={{ marginTop: 8, padding: 10, borderRadius: 9, background: "rgba(252,211,77,0.10)", border: "1px solid rgba(252,211,77,0.32)", color: "#fcd34d", fontSize: 11 }}>
            {data.note || "Full Support Mode launches in Turn C."}
          </div>
        </Card>

        <Card title={`Recent sessions · ${data.recent?.length ?? 0}`} style={{ gridColumn: "1 / -1" }}>
          {(!data.recent || data.recent.length === 0) ? (
            <EmptyState title="No previous support sessions" />
          ) : (
            <table style={tableStyles.table}>
              <thead><tr>
                <th style={tableStyles.th}>Started</th>
                <th style={tableStyles.th}>Ended</th>
                <th style={tableStyles.th}>Admin</th>
                <th style={tableStyles.th}>Workspace</th>
                <th style={tableStyles.th}>Status</th>
              </tr></thead>
              <tbody>
                {data.recent.map((s) => (
                  <tr key={s.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={{ ...tableStyles.td, fontSize: 11 }}>{(s.started_at || "").slice(0, 19)}</td>
                    <td style={{ ...tableStyles.td, fontSize: 11 }}>{(s.ended_at || "—").slice(0, 19)}</td>
                    <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(s.actor_user_id || "").slice(0, 16)}</td>
                    <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(s.workspace_id || "").slice(0, 16)}</td>
                    <td style={tableStyles.td}><Pill tone={s.status === "active" ? "success" : "muted"}>{s.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

function Tile({ icon, title, body }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px dashed var(--pl-border)" }}>
      <span style={{ color: "var(--pl-aqua)", marginTop: 2 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 800 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 2, lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}
